import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Building2, User, MapPin } from 'lucide-react';
import { tenantService, locationService } from '../../services';
import type { CreateTenantRequest, DropdownItem, CountryItem, StateItem, CityItem, Location } from '../../types';

const CreateTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dropdown data
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  const [cities, setCities] = useState<CityItem[]>([]);
  const [businessTypes, setBusinessTypes] = useState<DropdownItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  // Loading states for cascading dropdowns
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Form data
  const [formData, setFormData] = useState<CreateTenantRequest>({
    company_name: '',
    email: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    country: 0,
    state: 0,
    city: 0,
    location_ids: [],
    postal_code: '',
    business_type: 0,
    first_name: '',
    last_name: '',
    password_hash: '',
    secondary_email: '',
  });

  // Load initial dropdown data
  useEffect(() => {
    const loadDropdowns = async () => {
      setIsLoading(true);
      try {
        const [countriesRes, businessTypesRes, locationsRes] = await Promise.all([
          tenantService.getCountries(),
          tenantService.getBusinessTypes(),
          locationService.getLocations(),
        ]);
        setCountries(countriesRes.items || []);
        setBusinessTypes(businessTypesRes.items || []);
        setLocations(locationsRes.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dropdown data');
      } finally {
        setIsLoading(false);
      }
    };
    loadDropdowns();
  }, []);

  // Load states when country changes
  const handleCountryChange = async (countryId: number) => {
    setFormData(prev => ({ ...prev, country: countryId, state: 0, city: 0 }));
    setStates([]);
    setCities([]);

    if (countryId > 0) {
      setIsLoadingStates(true);
      try {
        const statesRes = await tenantService.getStates(countryId);
        setStates(statesRes.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load states');
      } finally {
        setIsLoadingStates(false);
      }
    }
  };

  // Handle business type change
  const handleBusinessTypeChange = (businessTypeId: number) => {
    setFormData(prev => ({ ...prev, business_type: businessTypeId }));
  };

  // Load cities when state changes
  const handleStateChange = async (stateId: number) => {
    setFormData(prev => ({ ...prev, state: stateId, city: 0 }));
    setCities([]);

    if (stateId > 0) {
      setIsLoadingCities(true);
      try {
        const citiesRes = await tenantService.getCities(stateId);
        setCities(citiesRes.items || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load cities');
      } finally {
        setIsLoadingCities(false);
      }
    }
  };

  const handleInputChange = (field: keyof CreateTenantRequest, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationToggle = (locationId: number) => {
    setFormData(prev => {
      const currentIds = prev.location_ids || [];
      const isSelected = currentIds.includes(locationId);
      return {
        ...prev,
        location_ids: isSelected
          ? currentIds.filter(id => id !== locationId)
          : [...currentIds, locationId]
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.company_name.trim()) {
      setError('Tenant name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.country || formData.country === 0) {
      setError('Country is required');
      return;
    }
    if (!formData.state || formData.state === 0) {
      setError('State is required');
      return;
    }
    if (!formData.city || formData.city === 0) {
      setError('City is required');
      return;
    }
    if (!formData.business_type || formData.business_type === 0) {
      setError('Business type is required');
      return;
    }
    if (!formData.first_name.trim()) {
      setError('Admin first name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      setError('Admin last name is required');
      return;
    }
    if (!formData.password_hash.trim()) {
      setError('Password is required');
      return;
    }
    if (formData.password_hash.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSaving(true);
    try {
      await tenantService.createTenant(formData);
      setSuccess('Tenant created successfully!');
      setTimeout(() => {
        navigate('/tenants');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tenants')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Create New Tenant</h1>
            <p className="text-text-muted mt-1">Add a new sub-tenant to your organization</p>
          </div>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {success && (
          <Alert variant="success" message={success} />
        )}

        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <Building2 size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Company Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Tenant Name *"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter tenant name"
                  fullWidth
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="company@example.com"
                  fullWidth
                />
                <Input
                  label="Phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* Address Information */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <MapPin size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Address</h2>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <Input
                  label="Address Line 1"
                  value={formData.address_line_1}
                  onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                  placeholder="Street address"
                  fullWidth
                />
                <Input
                  label="Address Line 2"
                  value={formData.address_line_2}
                  onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                  placeholder="Apt, suite, unit, etc. (optional)"
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <Select
                  label="Country *"
                  value={formData.country}
                  onChange={(e) => handleCountryChange(Number(e.target.value))}
                  options={[
                    { label: 'Select Country', value: 0 },
                    ...countries.filter(c => c.countrycode !== 'ALL').map(c => ({ label: c.countryname, value: c.countryid }))
                  ]}
                  fullWidth
                />
                <Select
                  label="State/Province *"
                  value={formData.state}
                  onChange={(e) => handleStateChange(Number(e.target.value))}
                  options={[
                    { label: isLoadingStates ? 'Loading...' : 'Select State', value: 0 },
                    ...states.filter(s => s.statecode !== 'ALL').map(s => ({ label: s.statename, value: s.stateid }))
                  ]}
                  disabled={!formData.country || isLoadingStates}
                  fullWidth
                />
                <Select
                  label="City *"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', Number(e.target.value))}
                  options={[
                    { label: isLoadingCities ? 'Loading...' : 'Select City', value: 0 },
                    ...cities.filter(c => c.cityname !== 'All').map(c => ({ label: c.cityname, value: c.cityid }))
                  ]}
                  disabled={!formData.state || isLoadingCities}
                  fullWidth
                />
                <Input
                  label="Postal Code"
                  value={formData.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                  placeholder="e.g., T2P 1A1"
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Select
                  label="Business Type *"
                  value={formData.business_type}
                  onChange={(e) => handleBusinessTypeChange(Number(e.target.value))}
                  options={[
                    { label: 'Select Business Type', value: 0 },
                    ...businessTypes.map(bt => ({ label: bt.value, value: bt.key }))
                  ]}
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* Location Assignments */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Location Assignments</h2>
              </div>
              <p className="text-sm text-text-muted mb-4">
                Select which locations this tenant should have access to. Selected locations will be shared with the tenant (you retain ownership).
              </p>
              
              {isLoadingLocations ? (
                <div className="flex items-center justify-center py-8">
                  <Loading />
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  No locations available to assign
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {locations.map((location) => (
                    <label
                      key={location.ycl_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.location_ids?.includes(location.ycl_id)
                          ? 'border-accent-primary bg-accent-primary/5'
                          : 'border-border-primary hover:border-accent-primary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.location_ids?.includes(location.ycl_id) || false}
                        onChange={() => handleLocationToggle(location.ycl_id)}
                        className="w-4 h-4 text-accent-primary rounded focus:ring-accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-text-primary truncate">
                          {location.ycl_name}
                        </div>
                        {location.ycl_code && (
                          <div className="text-xs text-text-muted truncate">
                            {location.ycl_code}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {formData.location_ids && formData.location_ids.length > 0 && (
                <div className="mt-4 p-3 bg-info-bg border border-info-border rounded-lg">
                  <p className="text-sm text-info-text">
                    <strong>{formData.location_ids.length}</strong> location{formData.location_ids.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Admin User Information */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <User size={20} className="text-accent-primary" />
                <h2 className="text-lg font-semibold text-text-primary">Admin User</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name *"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  placeholder="Enter first name"
                  fullWidth
                />
                <Input
                  label="Last Name *"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  placeholder="Enter last name"
                  fullWidth
                />
                <Input
                  label="Password *"
                  type="password"
                  value={formData.password_hash}
                  onChange={(e) => handleInputChange('password_hash', e.target.value)}
                  placeholder="Minimum 8 characters"
                  helperText="Password must be at least 8 characters"
                  fullWidth
                />
                <Input
                  label="Secondary Email"
                  type="email"
                  value={formData.secondary_email}
                  onChange={(e) => handleInputChange('secondary_email', e.target.value)}
                  placeholder="backup@example.com (optional)"
                  fullWidth
                />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/tenants')}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Creating...' : 'Create Tenant'}
            </Button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default CreateTenantPage;
